import { Link } from "react-router-dom";
import { BedDouble, Bath, Ruler, MapPin, Car, PawPrint, Sofa, Accessibility, Check } from "lucide-react";
import { fmtRent, bedsLabel } from "../lib/api";

export default function ListingCard({ listing: l, match, selected, onToggleSelect, selectable }) {
  const img = l.images?.[0]?.url;
  return (
    <article data-testid={`listing-card-${l.listing_id}`}
      className="group rounded-2xl border border-teal-900/10 dark:border-teal-100/10 bg-card shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 overflow-hidden flex flex-col">
      <Link to={`/listings/${l.listing_id}`} className="block relative aspect-[4/3] overflow-hidden bg-secondary">
        {img ? (
          <img src={img} alt={l.images[0].alt || `${bedsLabel(l)} in ${l.neighborhood}`} loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted-foreground text-sm">Photos coming soon</div>
        )}
        {match != null && (
          <span data-testid={`match-badge-${l.listing_id}`}
            className={`absolute top-3 left-3 rounded-full px-3 py-1 text-xs font-bold shadow ${match === 100 ? "bg-primary text-primary-foreground" : "bg-card/90 backdrop-blur text-primary"}`}>
            {match === 100 ? "Perfect match" : `${match}% match`}
          </span>
        )}
        {l.promotions && (
          <span className="absolute bottom-3 left-3 rounded-full bg-accent text-accent-foreground px-3 py-1 text-xs font-semibold shadow">{l.promotions}</span>
        )}
      </Link>
      <div className="p-5 flex flex-col gap-3 flex-1">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="font-heading font-bold text-2xl tracking-tight">{fmtRent(l.monthly_rent)}<span className="text-sm font-medium text-muted-foreground">/mo</span></p>
            <p className="text-sm text-muted-foreground mt-0.5 flex items-center gap-1">
              <MapPin className="w-3.5 h-3.5" /> {l.neighborhood}{l.address_street ? ` · ${l.address_street}` : ""}
            </p>
            <p data-testid={`managed-by-${l.listing_id}`} className="text-xs text-muted-foreground mt-1">
              {l.company_type === "individual_landlord" ? "Listed by" : "Managed by"} <span className="font-medium text-foreground">{l.company_name || "Independent owner"}</span>
            </p>
          </div>
          {selectable && (
            <button data-testid={`select-listing-${l.listing_id}`} onClick={() => onToggleSelect(l.listing_id)}
              aria-pressed={selected} aria-label={selected ? "Remove from viewing selection" : "Select for viewing"}
              className={`shrink-0 rounded-full px-3.5 py-1.5 text-xs font-semibold border transition-colors ${selected ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary text-foreground"}`}>
              {selected ? <span className="inline-flex items-center gap-1"><Check className="w-3.5 h-3.5" /> Selected</span> : "Select for viewing"}
            </button>
          )}
        </div>
        <div className="flex items-center gap-4 text-sm text-muted-foreground">
          <span className="inline-flex items-center gap-1.5"><BedDouble className="w-4 h-4" /> {bedsLabel(l)}</span>
          <span className="inline-flex items-center gap-1.5"><Bath className="w-4 h-4" /> {l.baths} bath</span>
          {l.square_feet && <span className="inline-flex items-center gap-1.5"><Ruler className="w-4 h-4" /> {l.square_feet} ft²</span>}
        </div>
        <div className="flex flex-wrap gap-1.5 mt-auto">
          {l.furnished === "furnished" && <Tag icon={<Sofa className="w-3 h-3" />} label="Furnished" />}
          {l.parking !== "none" && <Tag icon={<Car className="w-3 h-3" />} label="Parking" />}
          {l.pet_friendly === "yes" && <Tag icon={<PawPrint className="w-3 h-3" />} label="Pet friendly" />}
          {l.accessibility === "accessible" && <Tag icon={<Accessibility className="w-3 h-3" />} label="Accessible" />}
          {l.date_available && <Tag label={`Available ${l.date_available}`} />}
        </div>
      </div>
    </article>
  );
}

function Tag({ icon, label }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-secondary text-secondary-foreground px-2.5 py-1 text-xs font-medium">{icon}{label}</span>
  );
}
